import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BarraProgresso from '../components/BarraProgresso';
import CronogramaVisual from '../components/CronogramaVisual';
import Documentos from './Documentos/Documentos';
import ClienteDocumentosView from './Documentos/ClienteDocumentosView';
import './ProjetoDetalhe.css';

interface Atividade {
  id: string;
  nome: string;
  descricao?: string;
  progresso: number;
  totalFotos: number;
}

interface Etapa {
  id: string;
  nome?: string;
  tipoEtapaNome?: string;
  ordem: number;
  progresso: number;
  atividades?: Atividade[];
}

interface ProjetoDetalhado {
  id: string;
  nome: string;
  tipo?: 'projeto' | 'obra';
  endereco: string;
  dataInicio: string;
  dataConclusao?: string;
  dataPrevistaConclusao?: string;
  etapas: Etapa[];
}

function getStatusLabel(progresso: number): { label: string; className: string } {
  if (progresso === 0) return { label: 'Não Iniciada', className: 'nao-iniciada' };
  if (progresso === 100) return { label: 'Concluída', className: 'concluida' };
  return { label: 'Em Andamento', className: 'em-andamento' };
}

function ProjetoDetalhe() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projeto, setProjeto] = useState<ProjetoDetalhado | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedEtapas, setExpandedEtapas] = useState<Set<string>>(new Set());
  const [loadingAtividades, setLoadingAtividades] = useState<Set<string>>(new Set());
  const [novaAtividade, setNovaAtividade] = useState<{ etapaId: string; nome: string; descricao: string } | null>(null);
  const [savingAtividade, setSavingAtividade] = useState(false);
  const [editandoAtividade, setEditandoAtividade] = useState<{ id: string; etapaId: string; nome: string } | null>(null);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [confirmExcluirAtividade, setConfirmExcluirAtividade] = useState<{ id: string; etapaId: string; nome: string } | null>(null);
  const [excluindoAtividade, setExcluindoAtividade] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Recarrega as atividades de uma etapa e atualiza o estado do projeto
  async function recarregarAtividades(etapaId: string) {
    const atividades = await api.get(`/etapas/${etapaId}/atividades`);
    setProjeto((prev) =>
      prev
        ? { ...prev, etapas: prev.etapas.map((e) => (e.id === etapaId ? { ...e, atividades } : e)) }
        : prev,
    );
  }

  async function handleSalvarEdicaoAtividade() {
    if (!editandoAtividade || !editandoAtividade.nome.trim()) return;
    setSalvandoEdicao(true);
    try {
      await api.put(`/atividades/${editandoAtividade.id}`, { nome: editandoAtividade.nome.trim() });
      const etapaId = editandoAtividade.etapaId;
      setEditandoAtividade(null);
      await recarregarAtividades(etapaId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao editar atividade');
    } finally {
      setSalvandoEdicao(false);
    }
  }

  async function handleExcluirAtividade() {
    if (!confirmExcluirAtividade) return;
    setExcluindoAtividade(true);
    try {
      await api.delete(`/atividades/${confirmExcluirAtividade.id}`);
      const etapaId = confirmExcluirAtividade.etapaId;
      setConfirmExcluirAtividade(null);
      await recarregarAtividades(etapaId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir atividade');
    } finally {
      setExcluindoAtividade(false);
    }
  }

  const isMaster = user?.tipo === 'Master';
  // Editar o projeto: apenas Arquiteto e Master (Assistente não edita o projeto)
  const podeEditarProjeto = user?.tipo === 'Arquiteto' || isMaster;
  // Ações de campo (apontar progresso, adicionar atividade): Assistente e Master
  const podeAcoesCampo = user?.tipo === 'Assistente' || isMaster;
  // Rota de edição: Arquiteto usa /painel, Master usa /admin
  const rotaEdicao =
    user?.tipo === 'Arquiteto'
      ? `/painel/empreendimentos/${id}/editar`
      : `/admin/empreendimentos/${id}/editar`;

  // Rótulo contextual conforme o tipo do empreendimento
  const rotuloTipo = projeto?.tipo === 'projeto' ? 'projeto' : 'obra';

  // Destino após exclusão, conforme o tipo de usuário
  const rotaAposExcluir =
    user?.tipo === 'Arquiteto' ? '/painel' : `/admin/empreendimentos?tipo=${rotuloTipo}`;

  async function handleDelete() {
    if (!id) return;
    setDeleting(true);
    try {
      await api.delete(`/empreendimentos/${id}`);
      navigate(rotaAposExcluir);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir empreendimento');
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  useEffect(() => {
    async function fetchProjeto() {
      try {
        setLoading(true);
        const data = await api.get(`/empreendimentos/${id}`);
        setProjeto(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar projeto');
      } finally {
        setLoading(false);
      }
    }
    if (id) fetchProjeto();
  }, [id]);

  const toggleEtapa = async (etapaId: string) => {
    setExpandedEtapas((prev) => {
      const next = new Set(prev);
      if (next.has(etapaId)) {
        next.delete(etapaId);
      } else {
        next.add(etapaId);
      }
      return next;
    });

    // Load activities if not already loaded
    if (!expandedEtapas.has(etapaId) && projeto) {
      const etapa = projeto.etapas.find((e) => e.id === etapaId);
      if (etapa && !etapa.atividades) {
        setLoadingAtividades((prev) => new Set(prev).add(etapaId));
        try {
          const atividades = await api.get(`/etapas/${etapaId}/atividades`);
          setProjeto((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              etapas: prev.etapas.map((e) =>
                e.id === etapaId ? { ...e, atividades } : e
              ),
            };
          });
        } catch {
          // Silently handle - user can retry by collapsing and expanding
        } finally {
          setLoadingAtividades((prev) => {
            const next = new Set(prev);
            next.delete(etapaId);
            return next;
          });
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="projeto-detalhe">
        <div className="projeto-detalhe-container">
          <p style={{ color: '#c9a96e', textAlign: 'center', marginTop: '2rem' }}>Carregando...</p>
        </div>
      </div>
    );
  }

  if (error || !projeto) {
    return (
      <div className="projeto-detalhe">
        <div className="projeto-detalhe-container">
          <p style={{ color: '#ff6b6b', textAlign: 'center', marginTop: '2rem' }}>
            {error || 'Projeto não encontrado'}
          </p>
        </div>
      </div>
    );
  }

  const progressoGeral =
    projeto.etapas.length > 0
      ? Math.round(
          projeto.etapas.reduce((acc, e) => acc + e.progresso, 0) /
            projeto.etapas.length
        )
      : 0;

  const etapasOrdenadas = [...projeto.etapas].sort((a, b) => a.ordem - b.ordem);

  return (
    <div className="projeto-detalhe">
      <div className="projeto-detalhe-container">
        <Link to="/empreendimentos" className="projeto-detalhe-back">
          ← Voltar aos projetos
        </Link>

        <header className="projeto-detalhe-header">
          <h1>{projeto.nome}</h1>
          <p className="projeto-detalhe-subtitle">{projeto.endereco}</p>
          {podeEditarProjeto && (
            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
              <Link
                to={rotaEdicao}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#c9a96e',
                  color: '#1a1a1a',
                  textDecoration: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minHeight: '44px',
                }}
              >
                ✏️ Editar {rotuloTipo === 'projeto' ? 'Projeto' : 'Obra'}
              </Link>
              <button
                onClick={() => setConfirmDelete(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  color: '#ff6b6b',
                  border: '1px solid #ff6b6b',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minHeight: '44px',
                  cursor: 'pointer',
                }}
              >
                🗑️ Excluir {rotuloTipo === 'projeto' ? 'Projeto' : 'Obra'}
              </button>
            </div>
          )}
        </header>

        <CronogramaVisual
          dataInicio={projeto.dataInicio}
          dataConclusao={projeto.dataPrevistaConclusao || projeto.dataConclusao || projeto.dataInicio}
          progressoFisico={progressoGeral}
        />

        <section aria-label="Etapas do projeto">
          {etapasOrdenadas.map((etapa) => {
            const isExpanded = expandedEtapas.has(etapa.id);
            const isLoadingActivities = loadingAtividades.has(etapa.id);
            const status = getStatusLabel(etapa.progresso);

            return (
              <div className="etapa-card" key={etapa.id}>
                <button
                  className="etapa-card-header"
                  onClick={() => toggleEtapa(etapa.id)}
                  aria-expanded={isExpanded}
                  aria-controls={`etapa-body-${etapa.id}`}
                >
                  <span
                    className={`etapa-card-expand-icon ${isExpanded ? 'expanded' : ''}`}
                    aria-hidden="true"
                  >
                    ▶
                  </span>
                  <div className="etapa-card-info">
                    <span className="etapa-card-nome">{etapa.tipoEtapaNome || etapa.nome}</span>
                    <div className="etapa-card-progresso">
                      <BarraProgresso value={etapa.progresso} />
                    </div>
                    <span className={`etapa-status-badge ${status.className}`}>
                      {status.label}
                    </span>
                  </div>
                </button>

                {isExpanded && (
                  <div
                    className="etapa-card-body"
                    id={`etapa-body-${etapa.id}`}
                    role="region"
                    aria-label={`Atividades de ${etapa.tipoEtapaNome || etapa.nome}`}
                  >
                    {isLoadingActivities ? (
                      <p style={{ color: '#c9a96e', padding: '1rem', fontSize: '0.85rem' }}>
                        Carregando atividades...
                      </p>
                    ) : etapa.atividades && etapa.atividades.length > 0 ? (
                      etapa.atividades.map((atividade) => (
                        <div className="atividade-item" key={atividade.id}>
                          {editandoAtividade?.id === atividade.id ? (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
                              <input
                                type="text"
                                value={editandoAtividade.nome}
                                autoFocus
                                onChange={(e) => setEditandoAtividade({ ...editandoAtividade, nome: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSalvarEdicaoAtividade(); if (e.key === 'Escape') setEditandoAtividade(null); }}
                                aria-label={`Editar nome de ${atividade.nome}`}
                                style={{ flex: 1, minWidth: '150px', padding: '0.5rem', borderRadius: '4px', border: '1px solid #4d4d4d', background: '#1a1a1a', color: '#fff', fontSize: '0.85rem' }}
                              />
                              <button
                                className="btn-apontamento"
                                disabled={salvandoEdicao || !editandoAtividade.nome.trim()}
                                onClick={(e) => { e.stopPropagation(); handleSalvarEdicaoAtividade(); }}
                              >
                                {salvandoEdicao ? '...' : '✓ Salvar'}
                              </button>
                              <button
                                style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', padding: '0.5rem', minHeight: '44px', minWidth: '44px' }}
                                onClick={(e) => { e.stopPropagation(); setEditandoAtividade(null); }}
                                aria-label="Cancelar edição"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="atividade-nome">{atividade.nome}</span>
                              <div className="atividade-progresso">
                                <BarraProgresso value={atividade.progresso} />
                              </div>
                              <span className="atividade-fotos">
                                📷 {atividade.totalFotos ?? 0} foto{(atividade.totalFotos ?? 0) !== 1 ? 's' : ''}
                              </span>
                              {podeAcoesCampo && (
                                <button
                                  className="btn-apontamento"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/admin/atividades/${atividade.id}/apontamento`); }}
                                  aria-label={`Registrar progresso em ${atividade.nome}`}
                                >
                                  📝 Apontar
                                </button>
                              )}
                              <button
                                className="btn-apontamento btn-historico"
                                onClick={(e) => { e.stopPropagation(); navigate(`/admin/atividades/${atividade.id}/historico`); }}
                                aria-label={`Ver histórico de ${atividade.nome}`}
                              >
                                📷 Histórico
                              </button>
                              {podeAcoesCampo && (
                                <button
                                  className="btn-apontamento"
                                  onClick={(e) => { e.stopPropagation(); setEditandoAtividade({ id: atividade.id, etapaId: etapa.id, nome: atividade.nome }); }}
                                  aria-label={`Editar atividade ${atividade.nome}`}
                                >
                                  ✏️ Editar
                                </button>
                              )}
                              {podeAcoesCampo && (
                                <button
                                  className="btn-apontamento"
                                  style={{ color: '#ff6b6b', borderColor: '#ff6b6b' }}
                                  onClick={(e) => { e.stopPropagation(); setConfirmExcluirAtividade({ id: atividade.id, etapaId: etapa.id, nome: atividade.nome }); }}
                                  aria-label={`Excluir atividade ${atividade.nome}`}
                                >
                                  🗑️ Excluir
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      ))
                    ) : (
                      <p style={{ color: '#999', padding: '1rem', fontSize: '0.85rem' }}>
                        Nenhuma atividade cadastrada
                      </p>
                    )}

                    {/* Botão adicionar atividade (Assistente / Master) */}
                    {podeAcoesCampo && (
                      <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #3a3a3a' }}>
                        {novaAtividade?.etapaId === etapa.id ? (
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                              type="text"
                              placeholder="Nome da atividade"
                              value={novaAtividade.nome}
                              onChange={(e) => setNovaAtividade({ ...novaAtividade, nome: e.target.value })}
                              style={{ flex: 1, minWidth: '150px', padding: '0.5rem', borderRadius: '4px', border: '1px solid #4d4d4d', background: '#1a1a1a', color: '#fff', fontSize: '0.85rem' }}
                            />
                            <button
                              className="btn-apontamento"
                              disabled={savingAtividade || !novaAtividade.nome.trim()}
                              onClick={async () => {
                                if (!novaAtividade.nome.trim()) return;
                                setSavingAtividade(true);
                                try {
                                  await api.post(`/etapas/${etapa.id}/atividades`, { nome: novaAtividade.nome, descricao: novaAtividade.descricao || undefined });
                                  setNovaAtividade(null);
                                  // Reload activities
                                  const atividades = await api.get(`/etapas/${etapa.id}/atividades`);
                                  setProjeto((prev) => prev ? { ...prev, etapas: prev.etapas.map((e) => e.id === etapa.id ? { ...e, atividades } : e) } : prev);
                                } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Erro ao criar atividade'); }
                                finally { setSavingAtividade(false); }
                              }}
                            >
                              {savingAtividade ? '...' : '✓ Salvar'}
                            </button>
                            <button
                              style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', padding: '0.5rem', minHeight: '44px', minWidth: '44px' }}
                              onClick={() => setNovaAtividade(null)}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn-apontamento"
                            onClick={() => setNovaAtividade({ etapaId: etapa.id, nome: '', descricao: '' })}
                          >
                            + Adicionar Atividade
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* Seção de Documentos do Projeto */}
        {id && (
          <section className="projeto-detalhe-documentos" aria-label="Documentos do projeto">
            {user?.tipo === 'Cliente' ? (
              <ClienteDocumentosView projetoId={id} />
            ) : (
              <Documentos projetoId={id} />
            )}
          </section>
        )}
      </div>

      {/* Modal de confirmação de exclusão de atividade */}
      {confirmExcluirAtividade && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-atividade-title"
          onClick={() => !excluindoAtividade && setConfirmExcluirAtividade(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'grid',
            placeItems: 'center',
            padding: '1rem',
            background: 'rgba(0,0,0,0.7)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(440px, 100%)',
              background: '#2d2d2d',
              border: '1px solid #3d3d3d',
              borderRadius: '12px',
              padding: '1.75rem',
              color: '#fff',
            }}
          >
            <h2 id="confirm-delete-atividade-title" style={{ margin: '0 0 0.75rem', color: '#ff6b6b', fontSize: '1.15rem' }}>
              Excluir atividade?
            </h2>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', lineHeight: 1.6, color: '#ddd' }}>
              Você está prestes a excluir a atividade <strong>{confirmExcluirAtividade.nome}</strong>. Esta ação é{' '}
              <strong style={{ color: '#ff6b6b' }}>irreversível</strong>.
            </p>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', lineHeight: 1.6, color: '#aaa' }}>
              Todo o histórico de apontamentos e as imagens enviadas nesta atividade serão apagados
              permanentemente e não poderão ser recuperados.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={() => setConfirmExcluirAtividade(null)}
                disabled={excluindoAtividade}
                style={{
                  padding: '0.6rem 1.2rem',
                  background: 'transparent',
                  color: '#ccc',
                  border: '1px solid #555',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  cursor: excluindoAtividade ? 'not-allowed' : 'pointer',
                  minHeight: '44px',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleExcluirAtividade}
                disabled={excluindoAtividade}
                style={{
                  padding: '0.6rem 1.2rem',
                  background: '#ff6b6b',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: excluindoAtividade ? 'wait' : 'pointer',
                  minHeight: '44px',
                }}
              >
                {excluindoAtividade ? 'Excluindo...' : 'Sim, excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {confirmDelete && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-title"
          onClick={() => !deleting && setConfirmDelete(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'grid',
            placeItems: 'center',
            padding: '1rem',
            background: 'rgba(0,0,0,0.7)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(480px, 100%)',
              background: '#2d2d2d',
              border: '1px solid #3d3d3d',
              borderRadius: '12px',
              padding: '1.75rem',
              color: '#fff',
            }}
          >
            <h2 id="confirm-delete-title" style={{ margin: '0 0 0.75rem', color: '#ff6b6b', fontSize: '1.2rem' }}>
              Excluir {rotuloTipo === 'projeto' ? 'projeto' : 'obra'} permanentemente?
            </h2>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', lineHeight: 1.6, color: '#ddd' }}>
              Você está prestes a excluir <strong>{projeto.nome}</strong>. Esta ação é{' '}
              <strong style={{ color: '#ff6b6b' }}>irreversível</strong>.
            </p>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', lineHeight: 1.6, color: '#aaa' }}>
              Todos os dados associados serão apagados permanentemente: etapas, atividades,
              apontamentos de progresso, fotos e documentos. Não será possível recuperar.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                style={{
                  padding: '0.6rem 1.2rem',
                  background: 'transparent',
                  color: '#ccc',
                  border: '1px solid #555',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  minHeight: '44px',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '0.6rem 1.2rem',
                  background: '#ff6b6b',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: deleting ? 'wait' : 'pointer',
                  minHeight: '44px',
                }}
              >
                {deleting ? 'Excluindo...' : 'Sim, excluir permanentemente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjetoDetalhe;
